import { ClearFiltersButton } from '@/shared/components/Filters';
import { cilSearch } from '@coreui/icons';
import CIcon from '@coreui/icons-react';
import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCol,
  CFormInput,
  CFormSelect,
  CInputGroup,
  CInputGroupText,
  CRow,
} from '@coreui/react';
import React, { useEffect, useState } from 'react';
import { brokenSubscriptionsApi } from '../../api';
import type {
  BrokenSubscriptionFilters,
  ProblemCodeOption,
  SubscriptionPlanStatus,
} from '../../types';
import { STATUS_OPTIONS } from './constants';

interface Props {
  filters: BrokenSubscriptionFilters;
  onFiltersChange: (filters: Partial<BrokenSubscriptionFilters>) => void;
  onClearFilters: () => void;
}

export const BrokenSubscriptionsFilters: React.FC<Props> = ({
  filters,
  onFiltersChange,
  onClearFilters,
}) => {
  const [searchInput, setSearchInput] = useState(filters.search || '');
  const [previousSearch, setPreviousSearch] = useState(filters.search || '');

  if ((filters.search || '') !== previousSearch) {
    setPreviousSearch(filters.search || '');
    setSearchInput(filters.search || '');
  }
  const [problemCodes, setProblemCodes] = useState<ProblemCodeOption[]>([]);
  const [problemCodesError, setProblemCodesError] = useState<string | null>(null);

  useEffect(() => {
    brokenSubscriptionsApi
      .getProblemCodes()
      .then((codes) => {
        setProblemCodes(codes);
        setProblemCodesError(null);
      })
      .catch(() => {
        setProblemCodesError('Failed to load problem codes');
      });
  }, []);

  const handleSearch = () => {
    onFiltersChange({ search: searchInput });
  };

  const isFiltersEmpty = () => {
    return !filters.search && !filters.problemCode && !filters.status;
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <CCard className="mb-3">
      <CCardBody>
        <CRow className="align-items-center mb-3">
          <CCol>
            <h5 className="mb-0">Filters</h5>
          </CCol>
          <CCol xs="auto">
            <ClearFiltersButton onClick={onClearFilters} disabled={isFiltersEmpty()} />
          </CCol>
        </CRow>

        <CRow className="g-3">
          <CCol md={6}>
            <CInputGroup className="mb-2">
              <CInputGroupText>
                <CIcon icon={cilSearch} />
              </CInputGroupText>
              <CFormInput
                placeholder="Search by Shopify ID, Name, or e-mail (partial string search supported)"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleKeyPress}
                className="border-end-0"
              />
              <CButton color="primary" onClick={handleSearch} className="border-start-0">
                Search
              </CButton>
            </CInputGroup>
          </CCol>

          <CCol md={3}>
            {problemCodesError && (
              <CAlert color="warning" className="mb-2 py-2 small">
                {problemCodesError}
              </CAlert>
            )}
            <CFormSelect
              className="mb-2"
              value={filters.problemCode || ''}
              onChange={(e) => onFiltersChange({ problemCode: e.target.value })}
              disabled={!!problemCodesError}
            >
              <option value="">All Problems</option>
              {problemCodes.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </CFormSelect>
          </CCol>

          <CCol md={3}>
            <CFormSelect
              className="mb-2"
              value={filters.status || ''}
              onChange={(e) =>
                onFiltersChange({ status: e.target.value as SubscriptionPlanStatus | '' })
              }
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </CFormSelect>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>
  );
};
